import 'numeral/locales/no';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { PickerUtils } from '@ditioas/ui-pickers';
import { polyfills } from '@ditioas/util-polyfills';
import { Toaster, TooltipProvider } from '@ditioas/web-ui';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dateEnLocale from 'date-fns/locale/en-US';
import dateNbLocale from 'date-fns/locale/nb';
import dateSvLocale from 'date-fns/locale/sv';
import { useSetAtom } from 'jotai';
import numeral from 'numeral';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation, withTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router';
import { OidcProvider } from 'redux-oidc';
import { SWRConfig } from 'swr';

import { setUserLanguageAtom } from 'hooks/useCurrentUserAndSettings';
import { get } from 'libs/apiClient';
import { defaultLocale, locales } from 'model/locale';

import App from './app';
import ErrorBoundary from './components/errors/error-boundary';
import { getUserManager } from './libs/auth';
import MediaQueryProvider from './providers/MediaQueryProvider';
import PusherProvider from './providers/pusher-provider';

/*
  oicd init
*/
const userManager = getUserManager();

userManager.events.addUserSignedOut(() => {
  userManager
    .signinSilent()
    .catch(() => {
      userManager.removeUser();
      userManager.signinRedirect();
    });
});

userManager.events.addSilentRenewError((error) => {
  const message = error?.message || error?.error || '';
  const requiresLogin =
    message.includes('login_required') ||
    message.includes('interaction_required') ||
    message.includes('invalid_grant');
  if (requiresLogin) {
    userManager.removeUser();
    userManager.signinRedirect();
  }
  // Transient errors (network, timeout) will be retried automatically by oidc-client
});

userManager.events.addAccessTokenExpired(() => {
  userManager.signinSilent().catch(() => {
    userManager.removeUser();
    userManager.signinRedirect();
  });
});

const dateLocales = {
  nb: dateNbLocale,
  en: dateEnLocale,
  sv: dateSvLocale,
};

/*
  Init i18n
 */
numeral.defaultFormat('0,0.00');
polyfills.run(defaultLocale);

const BoundaryWrapper = withTranslation()(({ t, ...rest }) => (
  <ErrorBoundary heading={t('Title.Global_error')}>
    <Content {...rest} />
  </ErrorBoundary>
));

const Content = ({ store, locale, ...rest }) => {
  const { t } = useTranslation();

  return (
    <SWRConfig
      value={{
        fetcher: (...args) =>
          get(...args).then((response) => {
            if (response?.status === 204) {
              const error = new Error(t('General.204Message'));
              error.response = response;
              throw error;
            }

            return response.json();
          }),
      }}
    >
      <OidcProvider store={store} userManager={userManager}>
        <PusherProvider>
          <LocalizationProvider
            dateAdapter={PickerUtils}
            adapterLocale={dateLocales[locale]}
          >
            <BrowserRouter>
              <MediaQueryProvider>
                <DndProvider backend={HTML5Backend}>
                  <TooltipProvider delayDuration={300}>
                    <App {...rest} locale={locale} />
                  </TooltipProvider>
                </DndProvider>
              </MediaQueryProvider>
            </BrowserRouter>
          </LocalizationProvider>
          <Toaster duration={1500} />
        </PusherProvider>
      </OidcProvider>
    </SWRConfig>
  );
};

Content.propTypes = {
  store: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
};

const setDateAndNumberLocale = (userLocale) => {
  let locale = 'no';
  if (userLocale === locales.EN) {
    locale = 'en';
  } else if (userLocale === locales.SV) {
    locale = 'sv';
  }

  numeral.locale(locale);
};

const AppWrapper = ({ i18n, store }) => {
  const [locale, setLocale] = useState(defaultLocale);
  const setUserLanguage = useSetAtom(setUserLanguageAtom);

  useEffect(() => {
    setDateAndNumberLocale(locale);
  }, [locale]);

  const handleOnLocaleChange = (newLoc, persist = false) => {
    const newLocale = Object.values(locales).includes(newLoc)
      ? newLoc
      : defaultLocale;

    setLocale(newLocale);

    i18n.changeLanguage(newLocale);
    if (persist) {
      setUserLanguage(newLocale);
    }
  };

  return (
    <BoundaryWrapper
      store={store}
      onLocaleChange={handleOnLocaleChange}
      locale={locale}
    />
  );
};

AppWrapper.propTypes = {
  store: PropTypes.object.isRequired,
  i18n: PropTypes.object.isRequired,
};

export default withTranslation()(AppWrapper);
